import {
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  Select,
  Button,
  message,
  Modal,
  Form,
  Input,
  DatePicker,
} from 'antd'
import { useEffect, useState, useMemo } from 'react'
import moment from 'moment'
import dayjs from 'dayjs'
import {
  getDashBoard,
  editPointSupply,
  getMarketplaces,
} from 'src/api/redeemusers'
import RedeemChart from 'src/components/Charts/RedeemChart'
import { fetchCountryProvider } from 'src/api/authen'
import { fetchUserInfo } from 'src/api/authen'
import { EditOutlined } from '@ant-design/icons'

const { Title } = Typography
const { RangePicker } = DatePicker

interface Product {
  id: string
  name: string
  providerCountry: string
}

const RedeemUserDashboard = () => {
  const [selectedCountryValue, setSelectedCountryValue] = useState<
    string | undefined
  >(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  )
  const [userId, setUserId] = useState('')
  const [loading, setLoading] = useState(false)
  const [dashboard, setDashboard] = useState(null)
  const [startDate, setStartDate] = useState(
    moment().startOf('day').subtract(5, 'week')
  )
  const [countries, setCountries] = useState([])
  const [products, setProducts] = useState<Product[]>([])

  const [endDate, setEndDate] = useState(moment().startOf('day'))

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const [supplyPoints, setSupplyPoints] = useState<number | null>(null)

  const [supplyDateTime, setSupplyDateTime] = useState<moment.Moment | null>(
    moment()
  )

  const filteredProducts = useMemo(() => {
    if (!selectedCountryValue) {
      return products
    }
    return products.filter(
      (product) => product.providerCountry === selectedCountryValue
    )
  }, [products, selectedCountryValue])

  const productOptions = useMemo(() => {
    return filteredProducts.map((product) => ({
      label: product.name,
      value: product.id,
    }))
  }, [filteredProducts])

  const fetchDashboard = async () => {
    try {
      setLoading(true)

      const response = await getDashBoard(
        selectedCountryValue,
        selectedProductId || '',
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      )
      setDashboard(response)
    } catch (err) {
      message.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const fetchProducts = async () => {
    try {
      const response = await getMarketplaces()
      setProducts(response)
    } catch (err) {
      message.error('Failed to load products', err)
    }
  }

  const userDetails = async () => {
    const userInfo = await fetchUserInfo()
    setUserId(userInfo?.data?.userId)
  }
  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = [
        { label: 'All', value: null },
        ...data.data.map((item) => ({
          label: item.countryName,
          value: item.countryCode,
        })),
      ]
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const _countryChange = (value: string) => {
    setSelectedCountryValue(value)
    setSelectedProductId(null)
  }
  const _productChange = (value: string) => {
    setSelectedProductId(value)
  }

  const mapChartData = (data = []) =>
    data.map((item) => ({
      date: item.time,
      count: item.count,
    }))

  const handleSaveSupplyPoints = async () => {
    if (!supplyPoints || !supplyDateTime) {
      message.error('Please enter points and date')
      return
    }

    try {
      setLoading(true)

      await editPointSupply(
        supplyPoints,
        userId,
        supplyDateTime.format('YYYY-MM-DDTHH:mm:ss')
      )

      message.success('Points supplied successfully')
      setEditModalOpen(false)
      setSupplyPoints(null)
      setSupplyDateTime(null)
      fetchDashboard()
    } catch (err) {
      message.error('Failed to update supply points')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
  }, [startDate, endDate, selectedCountryValue, selectedProductId])

  useEffect(() => {
    fetchDashboard()
    fetchCountry()
    userDetails()
    fetchProducts()
  }, [])

  return (
    <>
      <Title level={3}>Redeem Prize Dashboard</Title>
      <br />

      <Row gutter={16}>
        <Col span={5}>
          <Select
            placeholder="Select Product"
            style={{ width: '100%' }}
            value={selectedProductId}
            onChange={_productChange}
            options={productOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            allowClear
            onClear={() => {
              setSelectedProductId(null)
            }}
          />
        </Col>
        <Col span={5}>
          <Select
            value={selectedCountryValue}
            style={{ width: '100%' }}
            onChange={_countryChange}
            options={countries}
            loading={loading}
          >
            {countries.map((country) => (
              <Select.Option value={country.value} key={country.value}>
                {country.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={10}>
          <RangePicker
            allowClear={false}
            value={[startDate, endDate]}
            disabledDate={(current) =>
              current && current > dayjs().endOf('day')
            }
            onChange={(dates) => {
              if (dates) {
                setStartDate(dates[0])
                setEndDate(dates[1])
              } else {
                setStartDate(null)
                setEndDate(null)
              }
            }}
          />
        </Col>
        <Col span={4}>
          <Button
            type="primary"
            className="btn-primary"
            onClick={fetchDashboard}
            loading={loading}
          >
            Search
          </Button>
        </Col>
      </Row>
      <br />
      <Row gutter={16}>
        <Col span={5}>
          <Card>
            <Button
              icon={<EditOutlined />}
              className="btn-primary"
              type="primary"
              onClick={() => setEditModalOpen(true)}
              style={{
                float: 'right',
                marginTop: -20,
                marginRight: -20,
                width: '12%',
                height: '12%',
              }}
            />
            <Statistic
              title="Total Points Supply"
              value={dashboard?.totalPointsSupply ?? 0}
              formatter={() => (
                <span
                  style={{
                    color: '#3A9916AD',
                    fontSize: '3rem',
                    fontWeight: 600,
                  }}
                >
                  {dashboard?.totalPointsSupply ?? 0}
                </span>
              )}
            />
          </Card>
        </Col>

        <Col span={5}>
          <Card>
            <Statistic
              title="Total No. of Redemption"
              value={dashboard?.totalNoOfRedemption ?? 0}
              formatter={() => (
                <span
                  style={{
                    color: '#5c6ef2',
                    fontSize: '3rem',
                    fontWeight: 600,
                  }}
                >
                  {dashboard?.totalNoOfRedemption ?? 0}
                </span>
              )}
            />
          </Card>
        </Col>

        <Col span={5}>
          <Card>
            <Statistic
              title="Total Points Redeemed"
              value={dashboard?.totalPointsRedeem ?? 0}
              formatter={() => (
                <span
                  style={{
                    color: '#5c6ef2',
                    fontSize: '3rem',
                    fontWeight: 600,
                  }}
                >
                  {dashboard?.totalPointsRedeem ?? 0}
                </span>
              )}
            />
          </Card>
        </Col>

        <Col span={5}>
          <Card>
            <Statistic
              title="Total Points Holding"
              value={dashboard?.totalPointsHolding ?? 0}
              formatter={() => (
                <span
                  style={{
                    color: '#5c6ef2',
                    fontSize: '3rem',
                    fontWeight: 600,
                  }}
                >
                  {dashboard?.totalPointsHolding ?? 0}
                </span>
              )}
            />
          </Card>
        </Col>

        <Col span={4}>
          <Card>
            <Statistic
              title="Total Extra Points"
              value={dashboard?.totalExtraPointsHolding ?? 0}
              formatter={() => (
                <span
                  style={{
                    color: '#5c6ef2',
                    fontSize: '3rem',
                    fontWeight: 600,
                  }}
                >
                  {dashboard?.totalExtraPointsHolding ?? 0}
                </span>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={12}>
          <Card title="Total No. of Redemption">
            <RedeemChart
              data={mapChartData(dashboard?.noOfRedemption)}
              yAxisTitle="Total No. of Redemption"
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Total Points Redemption">
            <RedeemChart
              data={mapChartData(dashboard?.pointRedemption)}
              yAxisTitle="Total Points Redeemed"
            />
          </Card>
        </Col>
      </Row>
      <Modal
        title="Edit Points Supply"
        visible={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleSaveSupplyPoints}
        okText="Save"
        confirmLoading={loading}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            style={{
              width: '100%',
              color: 'rgb(233 233 233 / 85%)',
              background: 'black',
            }}
            placeholder="Enter points"
            value={supplyPoints}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) =>
              setSupplyPoints(Number(e.target.value.replace(/\D/g, '')))
            }
          />
        </div>

        <div>
          <DatePicker
            style={{
              width: '100%',
              color: 'rgb(233 233 233 / 85%)',
              background: 'black',
            }}
            value={supplyDateTime || moment()}
            onChange={(value) => setSupplyDateTime(value)}
          />
        </div>
      </Modal>
    </>
  )
}

export default RedeemUserDashboard
